/**
 * The DGB mark, exactly as the brand kit delivers it (Brand Kit 1.0.0 § The mark, decision 51).
 *
 * TWO FILES, NOT ONE RECOLOURED DRAWING. The kit ships every composition in blu brand and in
 * bianco, and the Brand Kit's first rule for the mark is to use the kit's files as delivered: no
 * recolouring, no redrawing. The console needs the blue version on the light theme and the white
 * one on the dark theme, so each component below renders BOTH files and `globals.css`
 * (`.brand-img-*`) shows the one the resolved theme needs. The hidden one is `display: none`, so it
 * is out of the accessibility tree as well as out of sight.
 *
 * This replaces the July component, which carried a different — now retired — mark as inline SVG
 * paths so it could inherit `currentColor` from the rail. The new mark has no colour to inherit:
 * the kit fixes it, so a file per colour is the faithful rendering rather than a workaround.
 *
 * The files are in `public/brand/`, copied byte for byte from the kit (the versioned copy is
 * `docs/reference/dgb-brand-kit/`). Height is set by the caller; the width follows each file's own
 * aspect, because the symbol is 187 × 160 and a forced square would letterbox it.
 */
import { cn } from '@/lib/utils';

type BrandImageProps = {
  readonly className?: string;
  /** Empty when the mark sits inside a control that already carries its own accessible name. */
  readonly alt?: string;
};

function BrandImage({
  composition,
  className,
  alt = 'DGB',
}: BrandImageProps & { readonly composition: string }) {
  return (
    <span className={cn('brand-img', className)}>
      <img className="brand-img-light" src={`/brand/${composition}-blu-brand.svg`} alt={alt} />
      <img className="brand-img-dark" src={`/brand/${composition}-bianco.svg`} alt={alt} />
    </span>
  );
}

/** Composition 10: the symbol and DGB on one line, without the payoff. The expanded rail. */
export function DgbLogo(props: BrandImageProps) {
  return <BrandImage composition="10-logo-senza-payoff" {...props} />;
}

/** Composition 06: the symbol alone. The compressed rail and small surfaces. */
export function DgbSymbol(props: BrandImageProps) {
  return <BrandImage composition="06-simbolo" {...props} />;
}

/**
 * Composition 01: the primary logo, with the payoff «Data Grounding Biotech» beneath. Only where
 * the payoff stays legible — the Brand Kit's floor is 48px tall — which today means sign-in.
 */
export function DgbLogoPrimary(props: BrandImageProps) {
  return <BrandImage composition="01-logo-primario" {...props} />;
}
